// src/components/knowledge-base/Header.jsx
import React, { useEffect, useState } from 'react';
import { AppBar, Toolbar, Box, Skeleton, Typography } from '@mui/material';
import { ReactComponent as Logo } from '../../../../assets/images/Trustchat1.svg';
import { ReactComponent as MobileLogo } from "../../../../assets/images/mobileLogo.svg";

const Header = ({
  onSignInClick,
  onStartClick,
  signInProps = {},
  startProps = {},
  logo,
  initialized,
  businessName,
  backgroundColor = '',
}) => {
  const [logoSrc, setLogoSrc] = useState(null);
  const [imgReady, setImgReady] = useState(false); // ← true only after new image fully loads

  const getLogoSrc = (url) => {
    if (!url) return null;
    try {
      const parsed = new URL(url);
      const imgUrl = parsed.searchParams.get('imgurl');
      return imgUrl ? decodeURIComponent(imgUrl) : url;
    } catch {
      return url;
    }
  };

  useEffect(() => {
    const newSrc = getLogoSrc(logo);
    setImgReady(false);   // ← immediately hide old logo when URL changes
    setLogoSrc(newSrc);
    if (!newSrc) setImgReady(true); // no logo → no image to wait for
  }, [logo]);

  // Show skeleton when not initialized OR image URL exists but hasn't loaded yet
  const showSkeleton = !initialized || (logoSrc && !imgReady);

  return (
    <AppBar
      position="static"
      elevation={0}
      sx={{
        boxShadow: 'none !important',
        border: 'none',
        borderBottom: 'none',
        outline: 'none',
        width: 'calc(100% + 448px)',
        backgroundColor: backgroundColor,
        paddingTop: { xs: '23px', sm: '25px', md: '25px', lg: '45px' },
        marginLeft: '-224px',
        marginRight: '-224px',
      }}
    >
      <Toolbar
        sx={{
          padding: '0 !important',
          minHeight: 'auto',
          paddingLeft: '224px !important',
          paddingRight: '224px !important',
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 0,
          }}
        >
          {/* LOGO SECTION */}
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: '8px', p: 0 }}>

            {/* SKELETON — shown while not initialized OR image loading */}
            {showSkeleton && (
              <>
                <Skeleton
                  variant="rectangular"
                  sx={{
                    height: { xs: 28, sm: 36, md: 44 },
                    width: { xs: 28, sm: 36, md: 44 },
                    borderRadius: '6px',
                    flexShrink: 0,
                  }}
                />
                <Skeleton
                  variant="text"
                  sx={{
                    height: { xs: 18, sm: 22, md: 28 },
                    width: { xs: 80, sm: 100, md: 130 },
                    borderRadius: '4px',
                  }}
                />
              </>
            )}

            {/* REAL CONTENT — in DOM always (so img preloads), but hidden until ready */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                visibility: showSkeleton ? 'hidden' : 'visible',
                position: showSkeleton ? 'absolute' : 'relative',
                pointerEvents: showSkeleton ? 'none' : 'auto',
              }}
            >
              {/* Real logo */}
              {logoSrc && (
                <Box
                  component="img"
                  key={logoSrc}
                  src={logoSrc}
                  alt="logo"
                  onLoad={() => setImgReady(true)}   // ← reveal only after loaded
                  onError={(e) => {
                    e.target.src = '/assets/images/Trustchat1.svg';
                    setImgReady(true);
                  }}
                  sx={{
                    height: { xs: 28, sm: 36, md: 44 },
                    width: { xs: 28, sm: 36, md: 44 },  // match height exactly
                    borderRadius: '50%',                  // use 50% not 100%
                    objectFit: 'cover',                   // cover fills the circle without distortion
                    display: 'block',
                    flexShrink: 0,                        // prevent squishing in flex containers
                  }}
                />
              )}

              {/* Default SVG when no logo */}
              {!logoSrc && initialized && (
                <MobileLogo style={{ display: 'block', height: { xs: 28, sm: 36, md: 44 }, width: { xs: 28, sm: 36, md: 44 } }} />
              )}

              {/* Business name */}
              <Typography
                noWrap
                sx={{
                  fontFamily: 'Inter, sans-serif',
                  fontSize: { xs: '14px', sm: '16px', md: '28px' },
                  fontWeight: 600,
                  color:
                    backgroundColor === '#F7F8F9' || backgroundColor === 'transparent'
                      ? '#0C141B'
                      : '#FFFFFF',
                  lineHeight: 1,
                  display: 'flex',
                  textTransform: 'capitalize',
                  alignItems: 'center',
                  minHeight: { xs: 28, sm: 36, md: 44 },
                }}
              >
                {businessName || 'Trustchat'}
              </Typography>
            </Box>

          </Box>

          {/* RIGHT SIDE BUTTONS (uncomment when needed) */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            {/* Sign In Button */}
            {/* <Button
              onClick={() => onSignInClick?.(signInProps)}
              sx={{
                borderRadius: '8px',
                width: '110px',
                textTransform: 'none',
                height: '50px',
                border: '1px solid #D5DCE1',
                background: '#FFF',
                color: '#405261',
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 600,
                boxShadow: '0 1px 2px 0 rgba(6, 21, 34, 0.05)',
                "&:hover": { backgroundColor: "#FFFFFF", borderColor: "#C5CED6" },
                "&.Mui-disabled": { color: "#93A1B0", border: "1px solid #C5CED6", bgcolor: "#F9FAFA" },
              }}
            >
              Sign in
            </Button> */}

            {/* Start for Free Button */}
            {/* <Button
              onClick={() => onStartClick?.(startProps)}
              sx={{
                borderRadius: '8px',
                width: '170px',
                textTransform: 'none',
                height: '50px',
                background: '#148DFD',
                color: '#FFF',
                fontFamily: 'Inter',
                fontSize: '16px',
                fontWeight: 600,
                boxShadow: '0 1px 2px 0 rgba(6, 21, 34, 0.05)',
                "&:hover": { backgroundColor: "#0077CC" },
              }}
            >
              Start for free
            </Button> */}
          </Box>
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;